var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/*
 * <vitesse-editable-description> — THE click-to-edit entity description.
 *
 * One element, every entity: a dashboard's board description, a controller's
 * annotation, a model's interface description — the gesture is learned once,
 * so it must not look or behave differently anywhere. The element owns the
 * interaction (click → inline input, Enter/blur commits, Escape abandons,
 * commit only on change); the PAGE owns persistence via `commit` — each entity
 * keeps its own write path (a board travels whole under its digest, a
 * controller annotates user_data, a model masks its interface), which is
 * exactly why the element takes a callback and not a transport.
 *
 * The hover affordance is a slowly-marching dashed blue ring (SVG, so the
 * dashes honor the app's border radius) — an ink-and-caret invitation rather
 * than the old grey slab. Styles: base-page.scss (.editable-description*).
 *
 * Light DOM: it renders inside .base-page-description and inherits the
 * subtitle type scale from it, exactly as the raw <p> did.
 */
import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
let VitesseEditableDescription = class VitesseEditableDescription extends LitElement {
    constructor() {
        super(...arguments);
        /** The current saved text ('' = none; `placeholder` shows instead). */
        this.value = '';
        /** Fallback line when there is no description yet. */
        this.placeholder = '';
        /** Tooltip on the editable line ("Click to edit"). */
        this.hint = '';
        /** When false, renders the plain read-only line (viewer lacks the permission,
         *  or the entity's description isn't writable). */
        this.canEdit = false;
        this.editing = false;
        this.onKey = (e) => {
            // Enter commits, Escape abandons. Blur commits too — the forgiving reading
            // of clicking away; the alternative loses a sentence somebody just typed
            // because they reached for the tab bar.
            if (e.key === 'Enter')
                e.target.blur();
            else if (e.key === 'Escape') {
                this.editing = false;
                e.stopPropagation();
            }
        };
        this.onBlur = (e) => {
            if (!this.editing)
                return; // Escape already abandoned this edit
            this.editing = false;
            const next = e.target.value.trim();
            if (next === this.value.trim())
                return;
            void this.commit?.(next);
        };
    }
    createRenderRoot() {
        return this;
    }
    render() {
        if (this.editing) {
            return html `<input
        class="editable-description-input"
        .value=${this.value}
        placeholder=${this.placeholder}
        autofocus
        @blur=${this.onBlur}
        @keydown=${this.onKey}
      />`;
        }
        const text = this.value || this.placeholder;
        if (!this.canEdit)
            return html `<p class="editable-description is-readonly">${text}</p>`;
        // The marching ring is an SVG so its dashes follow the rounded rectangle —
        // CSS dashed borders cannot animate their offset and gradients cannot honor
        // a radius. Hidden until hover (CSS).
        return html `<p class="editable-description" title=${this.hint} @click=${() => (this.editing = true)}>
      ${text}
      <svg class="editable-description-ring" aria-hidden="true">
        <rect x="1" y="1" rx="6" ry="6"></rect>
      </svg>
    </p>`;
    }
};
__decorate([
    property()
], VitesseEditableDescription.prototype, "value", void 0);
__decorate([
    property()
], VitesseEditableDescription.prototype, "placeholder", void 0);
__decorate([
    property()
], VitesseEditableDescription.prototype, "hint", void 0);
__decorate([
    property({ type: Boolean })
], VitesseEditableDescription.prototype, "canEdit", void 0);
__decorate([
    property({ attribute: false })
], VitesseEditableDescription.prototype, "commit", void 0);
__decorate([
    state()
], VitesseEditableDescription.prototype, "editing", void 0);
VitesseEditableDescription = __decorate([
    customElement('vitesse-editable-description')
], VitesseEditableDescription);
export { VitesseEditableDescription };
//# sourceMappingURL=vitesse-editable-description.js.map